import { Component, ComponentFactoryResolver, Directive, ElementRef, Inject, Injector, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigStorage, CrudEditorParentService, CrudEditorSelfService, CrudRowsEditDialogComponent, CrudRowsEditDialogConfig, CrudRowsEditDialogResponse, CrudRowsItemComponent, CrudRowsService, CrudRowsTypeInfo, ErrorInfoResolver, FileUploadSettings, FileUploadSettingsStorage, REPOSITORY_FILE_ROWS_TYPE, RepositoryFile, RepositoryFileReference, UploadFile, UserStorage, fileValidatorFn, formatFileSize, isRepositoryFileBrowserViewable, openUrl, ServerTimeService, requiredValidator, requiredIfValidator } from '@scisys-uim/uim-common';
import { AssetsUser, Firearm, FirearmDocumentType, RefCode } from 'assets-model';
import { FirearmDocumentService, FirearmDocumentRowsService, FirearmDocumentPermission, RefCodeStorage } from 'assets-service';



export type FirearmDocumentRowsComponentMode = 'LIST_DOCUMENTS' | 'UPLOAD_DOCUMENTS';

@Directive()
export abstract class FirearmDocumentRowsComponentBase<G> extends CrudRowsItemComponent<RepositoryFile, RepositoryFileReference, G> implements OnInit {


  defaultSort = null;
  defaultSortDirection = 'asc';
  displayedColumns = ['messageId', 'body', 'attributes', 'messageAttributes', 'actions'];



  protected systemConfig: any;

  rowsSelectedByButton: boolean = true;

  public form: FormGroup;

  uploadFiles: UploadFile[];

  fileUploadSettings: FileUploadSettings;
  public get uploadFileSizeLimit(): number {
    return this.fileUploadSettings?.maxFileSize;
  }
  blacklistedContentTypes: string[] = [];
  blacklistedExtensions: string[] = [];
  whitelistedContentTypes: string[] = [];
  whitelistedExtensions: string[] = [];

  private _mode: FirearmDocumentRowsComponentMode;
  get mode(): FirearmDocumentRowsComponentMode {
    if (this._mode == null) {
      this._mode = 'LIST_DOCUMENTS';
    }
    return this._mode;
  }
  @Input()
  set mode(val: FirearmDocumentRowsComponentMode) {
    if (val == null) {
      val = this.mode;
    }
    if (!this.modesAvailable.includes(val)) {
      val = this.modesAvailable[0];
    }
    this._mode = val;
  }

  private _modesAvailable: FirearmDocumentRowsComponentMode[];
  get modesAvailable(): FirearmDocumentRowsComponentMode[] {
    if (this._modesAvailable == null) {
      this._modesAvailable = ['LIST_DOCUMENTS', 'UPLOAD_DOCUMENTS'];
    }
    return this._modesAvailable;
  }
  @Input()
  set modesAvailable(val: FirearmDocumentRowsComponentMode[]) {
    if (val?.length == 0) {
      val = null;
    }
    this._modesAvailable = val;
    if (!this.modesAvailable.includes(this.mode)) {
      this._mode = this.modesAvailable[0];
    }
  }

  @Input()
  firearmDocumentsPermissions: string[];




  fileValidator = fileValidatorFn(this.rows, this.uploadFileSizeLimit, this.whitelistedContentTypes, this.whitelistedExtensions, this.blacklistedContentTypes, this.blacklistedExtensions);


  constructor(
    public firearmDocumentRowsService: CrudRowsService<RepositoryFile, RepositoryFileReference, G>,
    typeInfo: CrudRowsTypeInfo<RepositoryFile, RepositoryFileReference, G>,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected configStorage: ConfigStorage,
    protected userStorage: UserStorage,
    protected refCodeStorage: RefCodeStorage,
    protected firearmDocumentService: FirearmDocumentService,
    protected fileUploadSettingsStorage: FileUploadSettingsStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar,
    defaultRowCount?: number) {
    super(typeInfo, fb, elementRef, router, route, configStorage, userStorage, dialog, firearmDocumentRowsService, injector, snackbar, defaultRowCount);
    this.fileUploadSettingsStorage.nextNonNullItem().subscribe(settings => this.fileUploadSettings = settings);

  }

  uploadFilesChanged(files: UploadFile[]) {
    this.uploadFiles = files || [];
  }
  createForm(): FormGroup {
    var form = super.createForm();
    form.addControl('viewFirearmDocumentType', this.fb.control(null));
    return form;
  }

  createFormRow(row: RepositoryFile): FormGroup {
    return this.fb.group({
      contentType: [row.contentType],
      creationDate: [row.creationDate],
      filename: [row.filename],
      filePath: [row.filePath],
      lastModifiedDate: [row.lastModifiedDate],
      size: [row.size]
    });
  }

  createNewItem(): RepositoryFile {
    return <RepositoryFile>{
    };
  }

  sortingDataAccessor(item: RepositoryFile, columnDef: string) {
    // important to define these as adding when new rows, nulls MUST be sorted to the bottom
    // or the form controls will break

    return item[columnDef];

  }

  ngOnInit() {
    super.ngOnInit();

    this.addAllWarnings();

    function initOptions(config: any): any {
      config = config || {};
      return config;
    }
    this.systemConfig = initOptions({});
    this.configStorage.itemChanges().subscribe(config => {
      this.systemConfig = initOptions(config);
    });

    this.firearmDocumentService.getBlacklistedContentTypes().subscribe(types => this.blacklistedContentTypes = types);
    this.firearmDocumentService.getBlacklistedExtensions().subscribe(types => this.blacklistedExtensions = types);
    this.firearmDocumentService.getWhitelistedContentTypes().subscribe(types => this.whitelistedContentTypes = types);
    this.firearmDocumentService.getWhitelistedExtensions().subscribe(types => this.whitelistedExtensions = types);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  ngAfterContentChecked(): void {
    super.ngAfterContentChecked();
  }

  addAllWarnings(): void {
    super.addAllWarnings();
    this.addWarningDynamic(() => "Maxiumum file size: " + formatFileSize(this.uploadFileSizeLimit), () => this.mode == 'UPLOAD_DOCUMENTS' && this.uploadFileSizeLimit != null && this.uploadFileSizeLimit >= 0);
    this.addWarningDynamic(() => "Supported File Types: " + this.whitelistedExtensions.join(', '), () => this.mode == 'UPLOAD_DOCUMENTS' && this.whitelistedExtensions?.length > 0);
    this.addWarningDynamic(() => "Supported Content Types: " + this.whitelistedContentTypes.join(', '), () => this.mode == 'UPLOAD_DOCUMENTS' && this.whitelistedContentTypes?.length > 0);
    this.addWarningDynamic(() => "Forbidden File Types: " + this.blacklistedExtensions.join(', '), () => this.mode == 'UPLOAD_DOCUMENTS' && (this.whitelistedExtensions == null || this.whitelistedExtensions.length == 0) && this.blacklistedExtensions?.length > 0);
    this.addWarningDynamic(() => "Forbidden Content Types: " + this.blacklistedContentTypes.join(', '), () => this.mode == 'UPLOAD_DOCUMENTS' && (this.whitelistedContentTypes == null || this.whitelistedContentTypes.length == 0) && this.blacklistedContentTypes?.length > 0);
  }

  setDefaults() {
    if (!this.readOnly) {

      this.userStorage.nextNonNullItem().subscribe(userDetails => {

        // re-trigger updating drop-downs.
        this.rowsSet();
      });
    }
    this.setDefaultDisabledFields();

  }

  setDefaultDisabledFields() {
    super.setDefaultDisabledFields();
    this.form.enable();
    this.form.get('viewFirearmDocumentType').enable();
  }

  rowsSet() {
    super.rowsSet();
    if (this.selectedRowForm()) {
      this.selectedRowForm().markAsPristine();
    }
  }

  filtersDisabled() {
    return (this.selectedRow != null && this.isNewRow(this.selectedRow)) || this.selectedForm?.invalid || this.unsavedChanges();
  }


  dataSourceFilter(dataSource: RepositoryFile[]): RepositoryFile[] {
    return dataSource;
  }

  selectedRowChanged(row: RepositoryFile): void {
    super.selectedRowChanged(row);
    this.resetSelectionListSubscriptionsForRow(row);
  }

  protected resetSelectionListSubscriptionsForRow(row: RepositoryFile): void {
    const index = this.rowFormIndex(row);
  }

  isUploadFilesModeAvailable() {
    return this.modesAvailable?.includes('UPLOAD_DOCUMENTS');
  }

  isUploadFilesToggleAvailable() {
    if (!this.userDetails.hasAnyAuthority('UPLOAD_FIREARM_DOCUMENT')) {
      return false;
    }
    if (!(this.firearmDocumentsPermissions?.indexOf('UPLOAD') >= 0)) {
      return false;
    }
    if (!this.isUploadFilesModeAvailable()) {
      return false;
    }
    return true;
  }
  isUploadFilesToggleEnabled() {
    return true;
  }

  isUploadFilesAvailable() {
    return this.mode == 'UPLOAD_DOCUMENTS';
  }
  isUploadFilesEnabled() {
    return this.uploadFiles?.length > 0 && this.isUploadStartable();
  }

  getUploadStartableFiles() {
    return this.uploadFiles.filter(f => (f.progress == 0 && f.error == null) || (f.error != null && f.canRestart));
  }
  isUploadStartable() {
    return this.getUploadStartableFiles().length > 0;
  }

  getUploadInProgressFiles() {
    return this.uploadFiles.filter(f => f.progress > 0 && f.progress < 100 && f.error == null);
  }
  isUploadInProgress() {
    return this.getUploadInProgressFiles().length > 0;
  }

  getUploadErrorFiles() {
    return this.uploadFiles.filter(f => f.error != null);
  }
  isUploadError() {
    return this.getUploadErrorFiles().length > 0;
  }

  getUploadCompleteFiles() {
    return this.uploadFiles.filter(f => f.progress == 100 && f.error == null);
  }
  isUploadComplete() {
    return this.getUploadCompleteFiles().length > 0;
  }
}

@Directive()
export abstract class FirearmDocumentRowsComponentCommon<G> extends FirearmDocumentRowsComponentBase<G> implements FirearmDocumentParameters {
  disabledColumns: string[] = [];
  parentColumns: string[] = [];
  idColumns: string[] = ['filename'];

  displayedColumns = [
    'contentTypeIcon',
    'docType',
    'filename',
    'contentType',
    'creationDate',
    'size',
    'actions'];

  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  @Input()
  showIdColumns: boolean = true;

  @Input()
  showParentColumns: boolean = true;

  _showActions: string[] = [];
  @Input()
  set showActions(val: string[]) {
    this._showActions = val;
    this.actionsChanged();
  }
  get showActions(): string[] {
    if (!this._showActions) {
      this._showActions = [];
    }
    return this._showActions;
  }

  @Input()
  showFileLinks: boolean = true;

  get showActionsColumns(): boolean {
    return this.showActions.length > 0;
  }

  private _firearm: Firearm;
  get firearm(): Firearm {
    return this._firearm;
  }
  @Input()
  set firearm(val: Firearm) {
    this._firearm = val;
  }

  private _viewDocType: FirearmDocumentType;
  get viewDocType(): FirearmDocumentType {
    return this._viewDocType;
  }
  @Input()
  set viewDocType(val: FirearmDocumentType) {
    this._viewDocType = val;
  }

  private _uploadDocType: FirearmDocumentType;
  get uploadDocType(): FirearmDocumentType {
    return this._uploadDocType;
  }
  @Input()
  set uploadDocType(val: FirearmDocumentType) {
    this._uploadDocType = val;
  }

  private _allowSelectUploadDocType: boolean;
  get allowSelectUploadDocType(): boolean {
    return this._allowSelectUploadDocType;
  }
  @Input()
  set allowSelectUploadDocType(val: boolean) {
    this._allowSelectUploadDocType = val;
  }

  get assetId(): number {
    return this._firearm?.assetId;
  }

  public uploadForm: FormGroup;
  firearmDocumentTypeSelectionList: RefCode[];

  constructor(
    public firearmDocumentRowsService: FirearmDocumentRowsService,
    protected typeInfo: CrudRowsTypeInfo<RepositoryFile, RepositoryFileReference, G>,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected configStorage: ConfigStorage,
    protected userStorage: UserStorage,
    protected refCodeStorage: RefCodeStorage,
    protected firearmDocumentService: FirearmDocumentService,
    protected fileUploadSettingsStorage: FileUploadSettingsStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar,
    protected errorInfoResolver: ErrorInfoResolver) {
    super(firearmDocumentRowsService, typeInfo, fb, elementRef, router, route, configStorage, userStorage, refCodeStorage, firearmDocumentService, fileUploadSettingsStorage, dialog, injector, snackbar);

    this.unselectedRowsDisabled = false;
    this.clickSelectsRow = true;
    this.resetRowEnabledOnRowSelected = true;
    this.resetRowEnabledOnRowsSet = true;
    this.uploadForm = this.fb.group({
      uploadFirearmDocumentType: [null],
    },
      {
        validators: [requiredIfValidator(() => this.uploadFirearmDocumentType, () => this.allowSelectUploadDocType, 'Firearm Document Type', { required: "Firearm Document Type is required." })]
      });
    this.uploadFirearmDocumentTypeLoadSelectionList();
  }

  uploadFirearmDocumentTypeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('FIREARM_DOCUMENT_TYPE').subscribe(
      data => {
        this.firearmDocumentTypeSelectionList = data;
      }
    );
  }

  isUploadFilesEnabled() {
    return !this.uploadForm.invalid && super.isUploadFilesEnabled();
  }

  ngOnInit() {
    super.ngOnInit();

    // if we disable too soon, the column header display text won't be rendered, and the picker will have code names not display names.
    this.columns.$afterViewInit.subscribe(data => {
      this.setDisplayColumnsForContext();
    });
  }

  rowsSet() {
    super.rowsSet();
  }

  addShowAction(action: string) {
    if (action == null) {
      return;
    }
    if (!this.isShowAction(action)) {
      this.showActions.push(action);
      this.actionsChanged();
    }
  }

  isShowAction(action: string): boolean {
    if (action == null) {
      return false;
    }
    return this.showActions.includes(action);
  }

  actionsChanged() {
    this.setDisplayColumnsForContext();
  }

  protected setDisplayColumnsForContext() {
    if (!this.columns) {
      return;
    }
    var disabledColumns = [...this.disabledColumns];
    var explicitlyEnabledColumns = [];

    if (!this.showParentColumns) {
      disabledColumns = disabledColumns.concat(this.parentColumns);
    }
    if (!this.showIdColumns) {
      disabledColumns = disabledColumns.concat(this.idColumns);
    }
    if (!this.showActionsColumns) {
      disabledColumns.push('actions');
    } else {
      explicitlyEnabledColumns.push('actions');
    }
    this.columns.setColumnsEnabled(disabledColumns, false);
    this.columns.setColumnsEnabled(explicitlyEnabledColumns, true);
    // reset the defaults for the context so the picker Reset button doesn't re-enable the columns above
    this.columns.resetInitialDisplayColumns();
  }


  setFormRowEnabled(row: RepositoryFile, rowForm: FormGroup, enable: boolean) {
    if (enable) {
      rowForm.enable();

      const controls = rowForm.controls;
      for (const controlName in controls) {
        const control = controls[controlName];
        var enableControl = false;

        this.setControlEnabled(control, enableControl);
      }
    } else {
      rowForm.disable();
    }
  }

  isAddEnabled() {
    return false;
  }
  allowsAdd() {
    return false;
  }
  allowsDelete(): boolean {
    if (!(this.firearmDocumentsPermissions?.indexOf('DELETE') >= 0)) {
      return false;
    }
    if (!this.userStorage.currentUserHasAnyAuthority('DELETE_FIREARM_DOCUMENT')) {
      return false;
    }
    return true;
  }
  allowsDeleteRows(): boolean {
    return this.allowsDelete();
  }
  isDeleteEnabled(row: RepositoryFile) {
    return row?.deletable;
  }
  isDownloadEnabled(doc: RepositoryFile): boolean {
    return true;
  }
  isOpenEnabled(doc: RepositoryFile): boolean {
    return isRepositoryFileBrowserViewable(doc);
  }
  allowsDownload(): boolean {
    return this.userStorage.currentUserHasAnyAuthority('READ_FIREARM_DOCUMENT');
  }
  allowsOpen(): boolean {
    return this.userStorage.currentUserHasAnyAuthority('READ_FIREARM_DOCUMENT');
  }

  showInlineFileLinks(): boolean {
    return this.showFileLinks;
  }

  downloadDocument(doc: RepositoryFile) {
    this.openDocument(doc, true);
  }

  openDocument(doc: RepositoryFile, download: boolean = false) {
    if (!download && (!this.allowsOpen() || !this.isOpenEnabled(doc))) {
      return;
    }
    if (download && (!this.allowsDownload() || !this.isDownloadEnabled(doc))) {
      return;
    }
    const url: string = this.getDocumentUrl(doc, download);
    this.openDocumentUrl(url);
  }


  protected getDocumentUrl(doc: RepositoryFile, download: boolean): string {
    var docType = doc?.userAttributes?.firearmDocumentType != null ? doc.userAttributes.firearmDocumentType : this.viewDocType;
    return this.firearmDocumentRowsService.exportDocumentByFilename(this.assetId, docType, doc, download);
  }
  viewDocTypeChanged(event: any) {
    this.refreshRows();
  }

  refreshRows() {
    this.setRows([]);
    this.firearmDocumentRowsService.findAllDocuments(this.assetId, this.viewDocType).subscribe(data => {
      this.rows = data.items;
    });
  }

  public openDocumentUrl(url: string) {
    openUrl(url)
  }

  uploadAllFiles() {
    this.getUploadStartableFiles().forEach(uploadFile => {
      this.firearmDocumentRowsService.uploadFile(this.assetId, this.uploadDocType, uploadFile).subscribe(event => { }, error => {
        // This error handler is required, so that we absorb the error.
        // The error will be associated with the UploadFile by the framework,
        // and displayed in a more friendly way to the user,
        // BUT, we DON'T want the standard error popup showing,
        // hence this absorbing "catch" here
      });
    });
  }


  deleteRow(element: RepositoryFile): void {
    if (!element) {
      return;
    }
    this.firearmDocumentRowsService.deleteDocumentByFilename(element).subscribe(result => {
      this.refreshRows();
    });
  }

  get uploadFirearmDocumentType() {
    return this.uploadForm?.get('uploadFirearmDocumentType');
  }
}



// General purpose Dialog for all kinds of Firearm documents
export interface FirearmDocumentParameters {
  firearm: Firearm,
  viewDocType?: FirearmDocumentType,
  uploadDocType?: FirearmDocumentType,
  allowSelectUploadDocType: boolean,
  modesAvailable?: FirearmDocumentRowsComponentMode[];
  mode?: FirearmDocumentRowsComponentMode;
  permissions?: FirearmDocumentPermission[];
}

export interface FirearmDocumentEditDialogConfig<G> extends CrudRowsEditDialogConfig<RepositoryFile, RepositoryFileReference, G>, FirearmDocumentParameters {
  dialogTitle?: string;
  showActions?: string[];
}

@Component({
  selector: 'app-firearm-document-rows-edit-dialog',
  templateUrl: '../../../../../../../node_modules/@scisys-uim/uim-common/src/lib/crud-components/crud-rows-edit-dialog.component.html',
  styleUrls: ['../../../../../../../node_modules/@scisys-uim/uim-common/src/lib/crud-components/crud-rows-edit.component.css']
})
export class FirearmDocumentEditDialogComponent<G> extends CrudRowsEditDialogComponent<RepositoryFile, RepositoryFileReference, G> {
  constructor(dialogRef: MatDialogRef<FirearmDocumentEditDialogConfig<G>, CrudRowsEditDialogResponse<RepositoryFile, RepositoryFileReference, G>>,
    @Inject(MAT_DIALOG_DATA) protected editDialogConfig: FirearmDocumentEditDialogConfig<G>,
    route: ActivatedRoute,
    router: Router,
    userStorage: UserStorage,
    dialog: MatDialog,
    snackbar: MatSnackBar,
    componentFactoryResolver: ComponentFactoryResolver,
    injector: Injector) {
    super(dialogRef, editDialogConfig, route, router, userStorage, dialog, snackbar, componentFactoryResolver, injector);
  }

  get firearmDocumentRowsComponent(): FirearmDocumentRowsComponentCommon<number> {
    return <FirearmDocumentRowsComponentCommon<number>><unknown>this.rowsItemComponent;
  }

  get firearmDocumentService(): FirearmDocumentRowsService {
    return <FirearmDocumentRowsService><unknown>this.firearmDocumentRowsComponent.firearmDocumentRowsService;
  }

  get modesAvailable(): FirearmDocumentRowsComponentMode[] {
    return this.firearmDocumentRowsComponent.modesAvailable;
  }
  set modesAvailable(val: FirearmDocumentRowsComponentMode[]) {
    this.firearmDocumentRowsComponent.modesAvailable = val;
  }
  get mode(): FirearmDocumentRowsComponentMode {
    return this.firearmDocumentRowsComponent.mode;
  }
  set mode(val: FirearmDocumentRowsComponentMode) {
    this.firearmDocumentRowsComponent.mode = val;
  }

  get assetId(): number {
    return this.firearmDocumentRowsComponent.assetId;
  }
  get viewDocType(): FirearmDocumentType {
    return this.firearmDocumentRowsComponent.viewDocType;
  }
  get uploadDocType(): FirearmDocumentType {
    return this.firearmDocumentRowsComponent.uploadDocType;
  }

  ngOnInit() {
    super.ngOnInit();
    this.resetTitle();
    this.editDialogConfig.dialogTitle = this.editDialogConfig.dialogTitle || "Firearm Asset Documents";
    if (this.editDialogConfig.showActions) {
      this.firearmDocumentRowsComponent.showActions = this.editDialogConfig.showActions;
    }

    this.firearmDocumentRowsComponent.refreshRows();

    const self = this;

    const actionClasses = {
      'action-button': true
    };
    this.actions = [
      {
        get available(): boolean { return self.isDownloadZipAvailable() },
        get enabled(): boolean { return self.isDownloadZipEnabled() },
        color: 'accent2',
        buttonClasses: actionClasses,
        getTitle() {
          return 'Download as Zip';
        },
        doAction() {
          const url: string = self.firearmDocumentService.exportMultipleDocumentsAsZipByFileSpecification(self.assetId, self.viewDocType);
          self.firearmDocumentRowsComponent.openDocumentUrl(url);
        }
      },
      {
        get available(): boolean { return self.isUploadFilesToggleAvailable() },
        get enabled(): boolean { return self.isUploadFilesToggleEnabled() },
        color: 'primary',
        buttonClasses: actionClasses,
        getTitle() {
          return self.mode == 'UPLOAD_DOCUMENTS' ? 'View Files' : 'Upload Files';
        },
        doAction() {
          self.mode = (self.mode == 'UPLOAD_DOCUMENTS' ? 'LIST_DOCUMENTS' : 'UPLOAD_DOCUMENTS');
          if (self.mode == 'LIST_DOCUMENTS') {
            self.firearmDocumentRowsComponent.refreshRows();
          }
        }
      },
      {
        get available(): boolean { return self.isUploadFilesAvailable() },
        get enabled(): boolean { return self.isUploadFilesEnabled() },
        color: 'primary',
        buttonClasses: actionClasses,
        getTitle() {
          return 'Upload Chosen Files';
        },
        doAction() {
          self.firearmDocumentRowsComponent.uploadAllFiles();
        }
      }
    ];
  }

  onItemComponentSet() {
    super.onItemComponentSet();
    this.firearmDocumentRowsComponent.firearm = this.editDialogConfig.firearm;
    this.firearmDocumentRowsComponent.viewDocType = this.editDialogConfig.viewDocType;
    this.firearmDocumentRowsComponent.uploadDocType = this.editDialogConfig.uploadDocType;
    this.firearmDocumentRowsComponent.allowSelectUploadDocType = this.editDialogConfig.allowSelectUploadDocType;
    if (this.editDialogConfig.modesAvailable != null) {
      this.firearmDocumentRowsComponent.modesAvailable = this.editDialogConfig.modesAvailable;
    }
    if (this.editDialogConfig.mode != null) {
      this.firearmDocumentRowsComponent.mode = this.editDialogConfig.mode;
    }
    if (this.editDialogConfig.permissions != null) {
      this.firearmDocumentRowsComponent.firearmDocumentsPermissions = this.editDialogConfig.permissions;
    }
  }

  resetTitle(): void {
    this.editorTitle = this.editDialogConfig.dialogTitle;
  }

  isPersistenceEnabled() {
    return false;
  }

  allowsAdd(): boolean {
    return false;
  }
  allowsEditRows(): boolean {
    return false;
  }

  allowsConfirm(): boolean {
    return false;
  }

  openAllDocuments(download: boolean = false) {
    this.openDocuments(download);
  }

  openDocuments(download: boolean = false) {
    const url: string = this.firearmDocumentService.exportMultipleDocumentsAsZipByFileSpecification(this.assetId, this.viewDocType);
    this.firearmDocumentRowsComponent.openDocumentUrl(url);
  }

  isDownloadZipAvailable() {
    return this.mode == 'LIST_DOCUMENTS';
  }

  isDownloadZipEnabled() {
    return this.rows.length > 1;
  }

  isUploadFilesToggleAvailable() {
    return this.firearmDocumentRowsComponent.isUploadFilesToggleAvailable();
  }
  isUploadFilesToggleEnabled() {
    return this.firearmDocumentRowsComponent.isUploadFilesToggleEnabled();
  }

  isUploadFilesAvailable() {
    return this.firearmDocumentRowsComponent.isUploadFilesAvailable();
  }
  isUploadFilesEnabled() {
    return this.firearmDocumentRowsComponent.isUploadFilesEnabled();
  }
}





// rows component concrete classes for specific document types

// Specialisation for the only current type of document
@Component({
  selector: 'app-firearm-document-rows',
  templateUrl: './firearm-document-rows.component.html',
  styleUrls: ['./firearm-document-rows.component.scss'],
  providers: [CrudEditorParentService],
  viewProviders: [CrudEditorSelfService]
})
export class FirearmDocumentRowsComponent extends FirearmDocumentRowsComponentCommon<number> implements FirearmDocumentParameters {

  constructor(
    firearmDocumentService: FirearmDocumentService,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected configStorage: ConfigStorage,
    protected userStorage: UserStorage,
    protected refCodeStorage: RefCodeStorage,
    protected serverTimeService: ServerTimeService,
    protected fileUploadSettingsStorage: FileUploadSettingsStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar,
    protected errorInfoResolver: ErrorInfoResolver) {
    super(new FirearmDocumentRowsService(firearmDocumentService), REPOSITORY_FILE_ROWS_TYPE, fb, elementRef, router, route, configStorage, userStorage, refCodeStorage, firearmDocumentService, fileUploadSettingsStorage, dialog, injector, snackbar, errorInfoResolver);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.userStorage.nextNonNullItem().subscribe(userDetails => {
      this.setDisplayColumnsForContext();
    });
  }

  rowsSet(): void {
    super.rowsSet();
  }

  isUploadFilesToggleAvailable() {
    if (!super.isUploadFilesToggleAvailable()) {
      return false;
    }
    return true;
  }
}
